import { useSearchStore } from 'src/stores/search.js'
import { useUsersStore } from 'src/stores/users.js'

import { latestSearch } from 'src/services/latest_search/latest_search.js'

/**
 * suggest - generates a suggestor function to be used by emoji-input
 * data: object providing source information for specific types of suggestions:
 * data.emoji - optional, an array of all emoji available i.e.
 *   (useEmojiStore().standardEmojiList + state.instance.customEmoji)
 * data.users - optional, an array of all known users
 * updateUsersList - optional, a function to search and append to users
 *
 * Depending on data present one or both (or none) can be present, so if field
 * doesn't support user linking you can just provide only emoji.
 */

export default (data) => {
  const emojiCurry = suggestEmoji(data.emoji)
  const usersCurry = suggestUsers()
  return (input, nameKeywordLocalizer) => {
    const firstChar = input[0]
    if (firstChar === ':' && data.emoji) {
      return emojiCurry(input, nameKeywordLocalizer)
    }
    if (firstChar === '@' && usersCurry) {
      return usersCurry(input)
    }
    return []
  }
}

export const suggestEmoji = (emojis) => (input, nameKeywordLocalizer) => {
  const noPrefix = input.toLowerCase().substring(1)
  return emojis
    .map((emoji) => ({ ...emoji, ...nameKeywordLocalizer(emoji) }))
    .filter(
      (emoji) =>
        emoji.names
          .concat(emoji.keywords)
          .filter((kw) => kw.toLowerCase().includes(noPrefix)).length,
    )
    .map((k) => {
      let score = 0

      // An exact match always wins
      score += Math.max(
        ...k.names.map((name) => (name.toLowerCase() === noPrefix ? 200 : 0)),
        0,
      )

      // Prioritize custom emoji a lot
      score += k.imageUrl ? 100 : 0

      // Prioritize prefix matches somewhat
      score += Math.max(
        ...k.names.map((kw) =>
          kw.toLowerCase().startsWith(noPrefix) ? 10 : 0,
        ),
        0,
      )

      // Sort by length
      score -= k.displayText.length

      k.score = score
      return k
    })
    .sort((a, b) => {
      // Break ties alphabetically
      const alphabetically = a.displayText > b.displayText ? 0.5 : -0.5

      return b.score - a.score + alphabetically
    })
}

// when a user last posted, as a number; 0 when unknown (the app stores a
// Date, an invalid one when the server sent none)
const lastActive = (user) => new Date(user.last_status_at ?? 0).getTime() || 0

const startsWith = (text, prefix) =>
  !!text && text.toLowerCase().startsWith(prefix)

// Users for an @-mention being typed, best first: a handle match, then
// people you follow, then a display name match; then the more recently
// active. All matches are ranked before the list is cut.
export const rankUserSuggestions = (users, prefix, isFollowed = () => false) =>
  users
    .filter(
      (user) =>
        user.screen_name &&
        user.name &&
        (startsWith(user.screen_name, prefix) || startsWith(user.name, prefix)),
    )
    .map((user) => ({
      user,
      score:
        (startsWith(user.screen_name, prefix) ? 4 : 0) +
        (isFollowed(user) ? 2 : 0) +
        (startsWith(user.name, prefix) ? 1 : 0),
    }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        lastActive(b.user) - lastActive(a.user) ||
        a.user.screen_name.localeCompare(b.user.screen_name),
    )
    .slice(0, 20)
    .map(({ user }) => user)

export const suggestUsers = () => {
  // the last query and its suggestions, kept between calls
  let suggestions = []
  let previousQuery = ''

  // the server search fills the users store; the suggestions come from it
  const debounceUserSearch = latestSearch((query) =>
    useSearchStore().searchUsers({ query }),
  )

  return async (input) => {
    const noPrefix = input.toLowerCase().substr(1)
    if (previousQuery === noPrefix) return suggestions

    suggestions = []
    previousQuery = noPrefix
    // Fetch more and wait, don't fetch if there's the 2nd @ because
    // the backend user search can't deal with it.
    // Reference semantics make it so that we get the updated data after
    // the await.
    // null: a newer word replaced this one and fills the cache itself
    if (!noPrefix.includes('@')) {
      if ((await debounceUserSearch(noPrefix)) === null) return []
    }

    const users = useUsersStore()
    const newSuggestions = rankUserSuggestions(
      [...users.users.values()],
      noPrefix,
      (user) => users.relationship(user.id).following,
    ).map((user) => ({
      user,
      displayText: user.screen_name_ui,
      detailText: user.name,
      imageUrl: user.profile_image_url_original,
      replacement: '@' + user.screen_name + ' ',
    }))

    suggestions = newSuggestions || []
    return suggestions
  }
}
